/**
 * Sign in / create account dialog.
 *
 * The password rule mirrors the server's (10 characters minimum) so the user
 * is told before a round trip rather than after. The server still enforces it;
 * this is convenience, not validation.
 */

import { Session, type User } from '../session.ts';
import { button, buttons, errorLine, field, openDialog } from './dialog.ts';

const MIN_PASSWORD = 10;

export function showAccountDialog(onSignedIn: (user: User) => void): void {
  let mode: 'login' | 'register' = 'login';

  openDialog({
    title: 'Sign in to SirCAD',
    build(body, handle) {
      const form = document.createElement('form');
      form.className = 'modal-form';
      body.append(form);

      const nameInput = field(form, 'Name', 'text', { autocomplete: 'name' });
      const nameRow = nameInput.closest('label')!;
      nameRow.hidden = true;

      const email = field(form, 'Email', 'email', {
        required: true, autocomplete: 'username',
      });
      const password = field(form, 'Password', 'password', {
        required: true, autocomplete: 'current-password',
      });

      const hint = document.createElement('p');
      hint.className = 'modal-hint';
      hint.hidden = true;
      hint.textContent = `At least ${MIN_PASSWORD} characters.`;
      form.append(hint);

      const err = errorLine(form);

      const row = buttons(form);
      const toggle = button(row, 'Create an account instead', () => {
        mode = mode === 'login' ? 'register' : 'login';
        const registering = mode === 'register';
        handle.el.querySelector('h2')!.textContent =
          registering ? 'Create a SirCAD account' : 'Sign in to SirCAD';
        nameRow.hidden = !registering;
        hint.hidden = !registering;
        password.autocomplete = registering ? 'new-password' : 'current-password';
        toggle.textContent = registering
          ? 'I already have an account'
          : 'Create an account instead';
        submit.textContent = registering ? 'Create account' : 'Sign in';
        err.hidden = true;
      });

      const submit = button(row, 'Sign in', () => form.requestSubmit(), true);

      form.addEventListener('submit', (ev) => {
        ev.preventDefault();
        err.hidden = true;

        const mail = email.value.trim();
        const pass = password.value;

        if (!mail || !pass) {
          show(err, 'Enter your email and password.');
          return;
        }
        if (mode === 'register' && pass.length < MIN_PASSWORD) {
          show(err, `Password must be at least ${MIN_PASSWORD} characters.`);
          return;
        }

        submit.disabled = true;
        submit.textContent = mode === 'register' ? 'Creating…' : 'Signing in…';

        const work = mode === 'register'
          ? Session.register(mail, pass, nameInput.value.trim() || mail.split('@')[0]!)
          : Session.login(mail, pass);

        work
          .then((user) => {
            handle.close();
            onSignedIn(user);
          })
          .catch((e: Error) => {
            show(err, e.message);
            submit.disabled = false;
            submit.textContent = mode === 'register' ? 'Create account' : 'Sign in';
          });
      });

      // Focus the first empty field so returning users land on the password.
      queueMicrotask(() => email.focus());
    },
  });
}

function show(el: HTMLParagraphElement, message: string): void {
  el.textContent = message;
  el.hidden = false;
}
